import { useState, useEffect, FormEvent } from 'react';
import { trackEvent } from '../../../utils/analytics';

const FORM_URL = 'https://readdy.ai/api/form/d6uj3cnb245v7h34ocj0';

const recentRequests = [
  { city: 'Milano', product: 'Cassonetti EVO 150' },
  { city: 'Verona', product: 'Termiko® Kit' },
  { city: 'Modena', product: 'Controtelai Termici' },
  { city: 'Brescia', product: 'Monoblocco tapparelle' },
];

const trustItems = [
  { icon: 'ri-time-line', title: 'Risposta 48h', desc: 'Preventivo su misura reale' },
  { icon: 'ri-shield-check-line', title: 'Zero impegno', desc: 'Gratuito, nessun vincolo' },
  { icon: 'ri-user-voice-line', title: 'Tecnico diretto', desc: 'Non un bot, non un call center' },
  { icon: 'ri-award-line', title: 'Cert. Usb=1 W/m²K', desc: 'Doc. tecnica su richiesta' },
];

export default function FormSection() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [activeRequest, setActiveRequest] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveRequest((prev) => (prev + 1) % recentRequests.length);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const notes = form.querySelector<HTMLTextAreaElement>('textarea[name="note_tecniche"]');
    if (notes && notes.value.length > 500) return;
    setLoading(true);
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      trackEvent('form_submit', 'Form preventivo inviato', 'form_section');
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="form" className="bg-[#F6F6F6]">

      {/* Urgency bar */}
      <div className="bg-[#7FCDCD] px-5 py-2.5">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 flex-wrap">
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse flex-shrink-0"></span>
          <span className="text-white font-black text-xs whitespace-nowrap">
            PREVENTIVO GRATUITO — Risposta in 48 ore lavorative garantite
          </span>
          <span className="text-white/60 text-xs hidden sm:block">·</span>
          <span className="text-white/80 text-xs whitespace-nowrap">Nessun impegno. Parli con un tecnico, non con un bot.</span>
        </div>
      </div>

      <div className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">

            {/* LEFT */}
            <div className="flex flex-col gap-6">

              {/* Heading */}
              <div>
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="h-px w-8 bg-[#7FCDCD]"></div>
                  <span className="text-[#7FCDCD] text-xs font-bold uppercase tracking-widest" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    Preventivo su misura
                  </span>
                </div>
                <h2
                  className="text-3xl md:text-4xl lg:text-5xl font-black text-[#000000] leading-tight tracking-tighter mb-3"
                  style={{ fontFamily: "'Raleway', sans-serif" }}
                >
                  Dimmi di cosa hai bisogno.<br />
                  <span className="text-[#7FCDCD]">Ti rispondo io.</span>
                </h2>
                <p className="text-[#A5A5A5] text-sm leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  Un tecnico — non un bot — ti contatta con un preventivo specifico per il tuo cantiere.
                </p>
              </div>

              {/* Trust items 2x2 */}
              <div className="grid grid-cols-2 gap-3">
                {trustItems.map((item) => (
                  <div key={item.title} className="flex items-start gap-3 bg-white rounded-xl p-3.5 border border-[#E5E5E5]">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#7FCDCD]/10 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-[#7FCDCD] text-sm`}></i>
                    </div>
                    <div>
                      <div className="text-[#000000] font-semibold text-xs leading-tight mb-0.5">{item.title}</div>
                      <div className="text-[#A5A5A5] text-xs leading-tight">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Live activity */}
              <div className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-[#E5E5E5]">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse flex-shrink-0"></span>
                <p className="text-xs">
                  <span className="text-[#000000] font-semibold">{recentRequests[activeRequest].city}</span>
                  <span className="text-[#A5A5A5]"> ha appena richiesto: </span>
                  <span className="text-[#7FCDCD] font-semibold">{recentRequests[activeRequest].product}</span>
                </p>
              </div>

              {/* Contacts */}
              <div className="flex flex-col sm:flex-row gap-3 pt-1 border-t border-[#E5E5E5]">
                <a
                  href="tel:+390519981801"
                  onClick={() => trackEvent('phone_click', '051 9981801 – sezione form', 'form_section')}
                  className="flex items-center gap-3 group cursor-pointer flex-1 bg-white rounded-xl px-4 py-3 border border-[#E5E5E5] hover:border-[#7FCDCD]/40 transition-colors"
                >
                  <div className="w-9 h-9 flex items-center justify-center bg-[#7FCDCD]/10 group-hover:bg-[#7FCDCD] rounded-lg flex-shrink-0 transition-colors">
                    <i className="ri-phone-fill text-[#7FCDCD] group-hover:text-white text-sm transition-colors"></i>
                  </div>
                  <div>
                    <div className="text-[#A5A5A5] text-xs">Chiama</div>
                    <div className="text-[#000000] font-black text-sm">051 9981801</div>
                  </div>
                </a>
                <a
                  href="https://wa.me/393939932439"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('whatsapp_click', 'WhatsApp – sezione form', 'form_section')}
                  className="flex items-center gap-3 group cursor-pointer flex-1 bg-white rounded-xl px-4 py-3 border border-[#E5E5E5] hover:border-green-400/40 transition-colors"
                >
                  <div className="w-9 h-9 flex items-center justify-center bg-green-500/10 group-hover:bg-green-500 rounded-lg flex-shrink-0 transition-colors">
                    <i className="ri-whatsapp-fill text-green-500 group-hover:text-white text-sm transition-colors"></i>
                  </div>
                  <div>
                    <div className="text-[#A5A5A5] text-xs">WhatsApp</div>
                    <div className="text-[#000000] font-black text-sm">+39 393 993 2439</div>
                  </div>
                </a>
              </div>
            </div>

            {/* RIGHT — form */}
            <div>
              {/* Social proof mini */}
              <div className="flex items-center gap-3 mb-3 px-1">
                <div className="flex -space-x-1.5 flex-shrink-0">
                  {['M', 'A', 'L', 'G'].map((initial, i) => (
                    <div key={i} className="w-6 h-6 rounded-full bg-[#7FCDCD] border-2 border-[#F6F6F6] flex items-center justify-center text-white text-xs font-black">
                      {initial}
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  {[1,2,3,4,5].map((s) => <i key={s} className="ri-star-fill text-yellow-400 text-xs"></i>)}
                  <span className="text-[#A5A5A5] text-xs ml-1">500+ professionisti del Nord Italia</span>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-5 md:p-8 border border-[#E5E5E5]">
                {submitted ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-[#7FCDCD]/10 rounded-full mb-4">
                      <i className="ri-check-double-line text-3xl text-[#7FCDCD]"></i>
                    </div>
                    <h3 className="text-xl font-black text-[#000000] mb-2" style={{ fontFamily: "'Raleway', sans-serif" }}>
                      Richiesta ricevuta.
                    </h3>
                    <p className="text-[#A5A5A5] text-sm max-w-xs leading-relaxed">
                      Ti ricontatta un tecnico entro 48 ore lavorative con un preventivo su misura.
                    </p>
                  </div>
                ) : (
                  <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <h3 className="text-lg font-black text-[#000000] mb-0.5" style={{ fontFamily: "'Raleway', sans-serif" }}>
                        Richiedi il tuo preventivo
                      </h3>
                      <p className="text-[#A5A5A5] text-xs">Risposta garantita entro 48 ore lavorative.</p>
                    </div>

                    {/* Nome + Ragione: 1 col mobile, 2 col sm+ */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-[#555555] uppercase tracking-wide mb-2">
                          Nome e Cognome <span className="text-[#7FCDCD]">*</span>
                        </label>
                        <input
                          type="text"
                          name="nome_cognome"
                          required
                          placeholder="Mario Rossi"
                          className="w-full border border-[#A5A5A5]/30 rounded-xl px-4 py-3.5 text-base focus:outline-none focus:border-[#7FCDCD] focus:ring-2 focus:ring-[#7FCDCD]/10 transition-all"
                          style={{ fontFamily: "'DM Sans', sans-serif" }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-[#555555] uppercase tracking-wide mb-2">
                          Ragione Sociale <span className="text-[#7FCDCD]">*</span>
                        </label>
                        <input
                          type="text"
                          name="ragione_sociale"
                          required
                          placeholder="Rossi Serramenti srl"
                          className="w-full border border-[#A5A5A5]/30 rounded-xl px-4 py-3.5 text-base focus:outline-none focus:border-[#7FCDCD] focus:ring-2 focus:ring-[#7FCDCD]/10 transition-all"
                          style={{ fontFamily: "'DM Sans', sans-serif" }}
                        />
                      </div>
                    </div>

                    {/* Tipo azienda + Regione */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-[#555555] uppercase tracking-wide mb-2">
                          Tipo azienda <span className="text-[#7FCDCD]">*</span>
                        </label>
                        <select
                          name="tipo_azienda"
                          required
                          className="w-full border border-[#A5A5A5]/30 rounded-xl px-4 py-3.5 text-base focus:outline-none focus:border-[#7FCDCD] focus:ring-2 focus:ring-[#7FCDCD]/10 transition-all bg-white appearance-none"
                          style={{ fontFamily: "'DM Sans', sans-serif" }}
                        >
                          <option value="">Seleziona...</option>
                          <option value="Serramentista">Serramentista</option>
                          <option value="Montatore">Montatore</option>
                          <option value="Artigiano">Artigiano</option>
                          <option value="Impresa Edile">Impresa Edile</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-[#555555] uppercase tracking-wide mb-2">
                          Regione <span className="text-[#7FCDCD]">*</span>
                        </label>
                        <select
                          name="regione"
                          required
                          className="w-full border border-[#A5A5A5]/30 rounded-xl px-4 py-3.5 text-base focus:outline-none focus:border-[#7FCDCD] focus:ring-2 focus:ring-[#7FCDCD]/10 transition-all bg-white appearance-none"
                          style={{ fontFamily: "'DM Sans', sans-serif" }}
                        >
                          <option value="">Seleziona...</option>
                          <option value="Lombardia">Lombardia</option>
                          <option value="Veneto">Veneto</option>
                          <option value="Emilia Romagna">Emilia Romagna</option>
                          <option value="Altra regione">Altra regione</option>
                        </select>
                      </div>
                    </div>

                    {/* Tel + Email */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-[#555555] uppercase tracking-wide mb-2">
                          Telefono <span className="text-[#7FCDCD]">*</span>
                        </label>
                        <input
                          type="tel"
                          name="telefono"
                          required
                          placeholder="+39 333 000 0000"
                          className="w-full border border-[#A5A5A5]/30 rounded-xl px-4 py-3.5 text-base focus:outline-none focus:border-[#7FCDCD] focus:ring-2 focus:ring-[#7FCDCD]/10 transition-all"
                          style={{ fontFamily: "'DM Sans', sans-serif" }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-[#555555] uppercase tracking-wide mb-2">
                          Email <span className="text-[#7FCDCD]">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="info@tuaazienda.it"
                          className="w-full border border-[#A5A5A5]/30 rounded-xl px-4 py-3.5 text-base focus:outline-none focus:border-[#7FCDCD] focus:ring-2 focus:ring-[#7FCDCD]/10 transition-all"
                          style={{ fontFamily: "'DM Sans', sans-serif" }}
                        />
                      </div>
                    </div>

                    {/* Prodotto */}
                    <div>
                      <label className="block text-xs font-bold text-[#555555] uppercase tracking-wide mb-2">
                        Prodotto di interesse <span className="text-[#7FCDCD]">*</span>
                      </label>
                      <select
                        name="prodotto_interesse"
                        required
                        className="w-full border border-[#A5A5A5]/30 rounded-xl px-4 py-3.5 text-base focus:outline-none focus:border-[#7FCDCD] focus:ring-2 focus:ring-[#7FCDCD]/10 transition-all bg-white appearance-none"
                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                      >
                        <option value="">Seleziona...</option>
                        <option value="Cassonetti in PVC EVO 150">Cassonetti PVC – Modello EVO 150</option>
                        <option value="Cassonetti in PVC Rehau">Cassonetti PVC – Modello Rehau</option>
                        <option value="Controtelai per finestre">Controtelai per finestre</option>
                        <option value="Termiko kit universale">Termiko® – Controtelaio kit universale</option>
                        <option value="Monoblocco per tapparelle">Monoblocco per tapparelle</option>
                        <option value="Piu prodotti">Più prodotti / Non so ancora</option>
                      </select>
                    </div>

                    {/* Note */}
                    <div>
                      <label className="block text-xs font-bold text-[#555555] uppercase tracking-wide mb-2">
                        Note tecniche
                        <span className="ml-1.5 text-[#A5A5A5]/60 font-normal normal-case tracking-normal">({charCount}/500)</span>
                      </label>
                      <textarea
                        name="note_tecniche"
                        rows={4}
                        maxLength={500}
                        onChange={(e) => setCharCount(e.target.value.length)}
                        placeholder="Misure, tipo di serramento, esigenze particolari..."
                        className="w-full border border-[#A5A5A5]/30 rounded-xl px-4 py-3.5 text-base focus:outline-none focus:border-[#7FCDCD] focus:ring-2 focus:ring-[#7FCDCD]/10 transition-all resize-none"
                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                      />
                      {charCount > 450 && (
                        <p className="text-xs text-[#7FCDCD] mt-1">{500 - charCount} caratteri rimanenti</p>
                      )}
                    </div>

                    {/* Trust signals */}
                    <div className="flex items-center gap-4 flex-wrap py-2 border-y border-[#F6F6F6]">
                      {[
                        { icon: 'ri-lock-line', text: 'Dati protetti' },
                        { icon: 'ri-time-line', text: 'Risposta 48h' },
                        { icon: 'ri-gift-line', text: 'Gratuito' },
                      ].map((t) => (
                        <div key={t.text} className="flex items-center gap-1.5 text-[#A5A5A5] text-xs whitespace-nowrap">
                          <i className={`${t.icon} text-[#7FCDCD]`}></i>
                          {t.text}
                        </div>
                      ))}
                    </div>

                    {/* CTA buttons — stacked on mobile, side by side on sm+ */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        type="submit"
                        disabled={loading}
                        className="flex items-center justify-center gap-2 w-full sm:flex-1 bg-[#7FCDCD] hover:bg-[#6bbaba] text-white font-black py-4 rounded-xl transition-all disabled:opacity-60 whitespace-nowrap text-base cursor-pointer"
                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                      >
                        {loading ? (
                          <><i className="ri-loader-4-line animate-spin"></i> Invio...</>
                        ) : (
                          <><i className="ri-send-plane-fill"></i> Invia preventivo</>
                        )}
                      </button>
                      <a
                        href="https://wa.me/393939932439?text=Ciao%2C%20sono%20interessato%20a%20un%20preventivo%20per%20cassonetti%20o%20controtelai."
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackEvent('whatsapp_click', 'WhatsApp bottone form', 'form_section')}
                        className="flex items-center justify-center gap-2 w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-6 rounded-xl transition-all whitespace-nowrap text-base cursor-pointer"
                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                      >
                        <i className="ri-whatsapp-line text-lg"></i>
                        WhatsApp
                      </a>
                    </div>
                  </form>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
